import React, { useState, useEffect, useMemo } from "react";
import ReactSelect from "react-select";
import { 
  listCitas, 
  createCita, 
  updateCita,
  listTratamientosByReceptor,
  listConsultorios,
  listServicios,
  listCitasActivas,
  listHorariosGenerales,
  updateConsultorioSilent,
  listCitasByFecha,
  listMedicos,
  listConfiguracion
} from "../api/odontologia";
import { listClienteReceptor } from "../api/clientesReceptor";
import { listUsuarios } from "../api/usuarios";
import { FacturacionFe, FacturacionTicket, FacturacionCCF } from "../api/ventas";
import { facturasCliente } from "../api/facturar";
import { showAlertError, showAlertExito, showConfirmAlert } from "../utils/alertHelper";
import Loader from "./Loader";
import { 
  FiPlus, FiSearch, FiEdit3, FiCalendar, FiClock, 
  FiUser, FiMessageSquare, FiActivity, FiBriefcase,
  FiChevronLeft, FiChevronRight, FiCheckCircle, FiInfo,
  FiAlertCircle, FiLayers, FiDollarSign, FiGrid
} from "react-icons/fi";

// ── Helpers de facturación ──
const TAX_MAP = {
  "20": { rate: 0.13, desc: "Impuesto al Valor Agregado 13%" },
  "C3": { rate: 0,    desc: "Impuesto al Valor Agregado (exportaciones) 0%" },
};
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const fmt    = (n) => (round2(Number(n) || 0)).toFixed(2);

const safeParseJSON = (data) => {
  if (!data) return [];
  try {
    let p = typeof data === "string" ? JSON.parse(data) : data;
    if (typeof p === "string") p = JSON.parse(p);
    return Array.isArray(p) ? p : [];
  } catch { return []; }
};

const CitasOdontologia = () => {
  const [loading, setLoading] = useState(true);
  const [citas, setCitas] = useState([]);
  const [config, setConfig] = useState({
    calendarios_cantidad: 1,
    tiempo_calendarios: 30
  });
  const [showModal, setShowModal] = useState(false);
  const [showQuickModal, setShowQuickModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // ── Estado para modal de facturación ──
  const [showFacturaModal, setShowFacturaModal] = useState(false);
  const [citaAFacturar, setCitaAFacturar] = useState(null);
  const [tipoDocumentos, setTipoDocumentos] = useState([]);
  const [tipoFactura, setTipoFactura] = useState("");
  const [clienteFactura, setClienteFactura] = useState(null); 
  const [facturando, setFacturando] = useState(false);
  
  // Catálogos
  const [pacientes, setPacientes] = useState([]);
  const [doctores, setDoctores] = useState([]);
  const [consultorios, setConsultorios] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [tratamientosPaciente, setTratamientosPaciente] = useState([]);
  const [serviciosSeleccionados, setServiciosSeleccionados] = useState([]);
  const [citasActivas, setCitasActivas] = useState([]);
  const [horariosBase, setHorariosBase] = useState([]);

  // Búsqueda
  const [buscar, setBuscar] = useState("");
  
  // Filtro de fecha (Citas Diarias) — El Salvador UTC-6
  const getTodaySV = () => {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/El_Salvador' }).format(new Date());
  };
  
  const formatDateToISO = (date) => {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const todayISO = getTodaySV();
  const [fechaFiltro, setFechaFiltro] = useState(todayISO);
  
  // Modal de Selección de horario
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [slotStep, setSlotStep] = useState(0); // 0: Fecha, 1: Horario
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  
  const formatDateLocal = (f) => {
    if (!f) return "N/A";
    const [y, m, d] = f.split("T")[0].split("-").map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  
  const [form, setForm] = useState({
    id_receptor: "",
    id_medico: "",
    id_consultorio: "",
    id_tratamiento: "",
    calendario: 1,
    fecha: todayISO,
    hora_inicio: "",
    hora_fin: "",
    estado: "programada",
    tipo_cita: "procedimiento",
    json: "",
    observaciones: ""
  });

  const fetchData = async (fecha = fechaFiltro) => {
    setLoading(true);
    try {
      const res = await listCitasByFecha(fecha);
      setCitas(res?.data || []);
    } catch (error) {
      console.error("Error al cargar citas:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTipoDocumentos = async () => {
    if (tipoDocumentos.length > 0) return;
    try {
      const res = await facturasCliente();
      const allowedCodes = ["88", "01", "03"];
      const docs = (res?.data || [])
        .filter(d => allowedCodes.includes((d.TipoDocumento || d.typeFact || d).codigo))
        .sort((a, b) => {
          const ca = (a.TipoDocumento || a.typeFact || a).codigo;
          const cb = (b.TipoDocumento || b.typeFact || b).codigo;
          return allowedCodes.indexOf(ca) - allowedCodes.indexOf(cb);
        });
      setTipoDocumentos(docs);
      if (docs.length > 0) {
        const ticket = docs.find(d => (d.TipoDocumento || d.typeFact || d).codigo === "88");
        const fe     = docs.find(d => (d.TipoDocumento || d.typeFact || d).codigo === "01");
        setTipoFactura((ticket || fe || docs[0]).codigo ?? (docs[0].TipoDocumento || docs[0].typeFact || docs[0]).codigo);
      }
    } catch (e) {
      console.error("Error al cargar tipos de documento:", e);
    }
  };

  const fetchCatalogos = async () => {
    try {
      const [resPacientes, resMedicos, resConsultorios, resServicios, resActivas, resHorarios] = await Promise.all([
        listClienteReceptor(),
        listMedicos(),
        listConsultorios(),
        listServicios(),
        listCitasActivas(),
        listHorariosGenerales()
      ]);
      setPacientes((Array.isArray(resPacientes) ? resPacientes : (resPacientes?.data || [])).map(p => ({ 
         value: p.id?.toString(), 
         label: p.nombre || "Desconocido",
         original: p 
      })));

      setDoctores((Array.isArray(resMedicos) ? resMedicos : (resMedicos?.data || [])).filter(m => m.status === 1).map(m => ({ 
        value: m.id?.toString(), 
        label: `${m.Usuario?.nombre || ""} ${m.Usuario?.apellido || ""}`.trim() || m.Usuario?.username || "Médico"
      })));

      setConsultorios((Array.isArray(resConsultorios) ? resConsultorios : (resConsultorios?.data || [])).map(c => ({ value: c.id?.toString(), label: c.nombre, original: c })));

      setServicios(Array.isArray(resServicios) ? resServicios : (resServicios?.data || []));
      setCitasActivas(Array.isArray(resActivas) ? resActivas : (resActivas?.data || []));
      setHorariosBase(Array.isArray(resHorarios) ? resHorarios : (resHorarios?.data || []));

      const resConfig = await listConfiguracion();
      if (resConfig && resConfig.estado === 1 && resConfig.data) {
        setConfig({
          calendarios_cantidad: resConfig.data.calendarios_cantidad || 1,
          tiempo_calendarios: resConfig.data.tiempo_calendarios || 30
        });
      }
    } catch (error) {
      console.error("Error al cargar catálogos:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchData(fechaFiltro);
  }, [fechaFiltro]);

  useEffect(() => {
    if (showModal || showQuickModal) {
      if (consultorios.length === 0 || doctores.length === 0) fetchCatalogos();
    }
  }, [showModal, showQuickModal]);

  useEffect(() => {
    if (form.id_receptor) cargarTratamientos(form.id_receptor);
    else setTratamientosPaciente([]);
  }, [form.id_receptor]);

  const cargarTratamientos = async (idReceptor) => {
    try {
      const res = await listTratamientosByReceptor(idReceptor);
      setTratamientosPaciente(res?.data || []);
    } catch (error) {
      console.error("Error al cargar tratamientos del paciente:", error);
    }
  };

  const handleOpenQuickModal = (cita) => {
    setForm({ 
      id_receptor: cita.id_receptor?.toString() || "",
      id_medico: cita.id_medico?.toString() || "",
      id_consultorio: cita.id_consultorio?.toString() || "",
      id_tratamiento: cita.id_tratamiento?.toString() || "",
      calendario: cita.calendario || 1,
      fecha: cita.fecha ? cita.fecha.split('T')[0] : todayISO,
      hora_inicio: cita.hora_inicio?.substring(0, 5) || "",
      hora_fin: cita.hora_fin?.substring(0, 5) || "",
      estado: cita.estado || "programada",
      tipo_cita: cita.tipo_cita || "procedimiento",
      json: cita.json || "",
      observaciones: cita.observaciones || ""
    });
    setServiciosSeleccionados(safeParseJSON(cita.json));
    setCurrentId(cita.id);
    setShowQuickModal(true);
  };

  const handleOpenModal = (cita = null) => {
    if (cita) {
      setForm({ 
        id_receptor: cita.id_receptor?.toString() || "",
        id_medico: cita.id_medico?.toString() || "",
        id_consultorio: cita.id_consultorio?.toString() || "",
        id_tratamiento: cita.id_tratamiento?.toString() || "",
        calendario: cita.calendario || 1,
        fecha: cita.fecha ? cita.fecha.split('T')[0] : todayISO,
        hora_inicio: cita.hora_inicio?.substring(0, 5) || "",
        hora_fin: cita.hora_fin?.substring(0, 5) || "",
        estado: cita.estado || "programada",
        tipo_cita: cita.tipo_cita || "procedimiento",
        json: cita.json || "",
        observaciones: cita.observaciones || ""
      });
      setServiciosSeleccionados(safeParseJSON(cita.json));
      setCurrentId(cita.id);
      setEditMode(true);
    } else {
      setForm({ 
        id_receptor: "", id_medico: "", id_consultorio: "", id_tratamiento: "",
        calendario: 1,
        fecha: todayISO,
        hora_inicio: "", hora_fin: "", estado: "programada", tipo_cita: "procedimiento",
        json: "", observaciones: ""
      });
      setServiciosSeleccionados([]);
      setEditMode(false);
      setCurrentId(null);
    }
    setShowModal(true);
  };

  const handleSelectChange = (val, name) => {
    setForm(prev => ({ ...prev, [name]: val ? val.value : "" }));
  };

  const handleAgregarProcedimiento = (seleccion) => {
    if (!seleccion) return;
    const existe = serviciosSeleccionados.find(s => s.id === seleccion.data.id);
    if (existe) handleCantidadChange(existe.id, existe.cantidad + 1);
    else setServiciosSeleccionados(prev => [...prev, { ...seleccion.data, cantidad: 1 }]);
  };

  const handleEliminarProcedimiento = (id) => {
    setServiciosSeleccionados(prev => prev.filter(s => s.id !== id));
  };

  const handleCantidadChange = (id, cant) => {
    const nVal = Math.max(1, parseInt(cant) || 1);
    setServiciosSeleccionados(prev => prev.map(s => s.id === id ? { ...s, cantidad: nVal } : s));
  };

  const totalDuracion = useMemo(() => {
    return serviciosSeleccionados.reduce((acc, s) => {
      const dur = parseInt(s.duracion_minutos) || 0;
      return acc + (dur * s.cantidad);
    }, 0);
  }, [serviciosSeleccionados]);

  useEffect(() => {
    if (form.hora_inicio && totalDuracion) {
      const [h, m] = form.hora_inicio.split(":").map(Number);
      const start = new Date(2000, 0, 1, h, m);
      const end = new Date(start.getTime() + totalDuracion * 60000);
      const horaFin = `${end.getHours().toString().padStart(2, '0')}:${end.getMinutes().toString().padStart(2, '0')}`;
      setForm(prev => ({ ...prev, hora_fin: horaFin }));
    }
  }, [form.hora_inicio, totalDuracion]);

  // --- LOGICA DE DISPONIBILIDAD AVANZADA ---
  const slotsDisponibles = useMemo(() => {
    const slots = [];
    const interval = config.tiempo_calendarios || 30;
    
    for (let h = 7; h <= 20; h++) {
      for (let m = 0; m < 60; m += interval) {
        slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
      }
    }

    const getDiaLocal = (f) => {
      const [y, m, d] = f.split("-").map(Number);
      return (new Date(y, m - 1, d)).getDay() === 0 ? 7 : (new Date(y, m - 1, d)).getDay();
    };

    const diaSemana = getDiaLocal(form.fecha);
    const rangosDia = horariosBase.filter(h => h.dia_semana === diaSemana && h.status === 1);

    return slots.map(time => {
      const [sh, sm] = time.split(":").map(Number);
      const slotStart = new Date(2000, 0, 1, sh, sm);
      const slotEnd = new Date(slotStart.getTime() + totalDuracion * 60000);
      
      const timeStr = `${sh.toString().padStart(2, '0')}:${sm.toString().padStart(2, '0')}:00`;
      const timeEndStr = `${slotEnd.getHours().toString().padStart(2, '0')}:${slotEnd.getMinutes().toString().padStart(2, '0')}:00`;

      const dentroDeRango = rangosDia.some(r => timeStr >= r.hora_inicio && timeEndStr <= r.hora_fin);

      const ocupados = citasActivas
        .filter(c => {
          if (c.id === currentId) return false;
          if (c.fecha !== form.fecha) return false;
          return (timeStr < (c.hora_fin || "23:59:00") && timeEndStr > c.hora_inicio);
        })
        .map(c => Number(c.calendario));

      const disponibles = [];
      for (let i = 1; i <= config.calendarios_cantidad; i++) {
        if (!ocupados.includes(i)) disponibles.push(i);
      }

      let motivo = "Libre";
      if (!dentroDeRango) {
        const iniciaEnRango = rangosDia.some(r => timeStr >= r.hora_inicio && timeStr < r.hora_fin);
        motivo = iniciaEnRango ? "Excede" : "Cerrado";
      } else if (disponibles.length === 0) {
        motivo = "Ocupado";
      }

      return { 
        time, 
        disponible: dentroDeRango && disponibles.length > 0, 
        motivo, 
        disponibles, 
        timeEndStr: `${slotEnd.getHours().toString().padStart(2, '0')}:${slotEnd.getMinutes().toString().padStart(2, '0')}:00` 
      };
    }).filter(s => s.motivo !== "Cerrado");
  }, [form.fecha, totalDuracion, citasActivas, horariosBase, currentId, config]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.id_receptor) return showAlertError("Error", "Debe seleccionar un paciente.");
    if (!form.hora_inicio) return showAlertError("Error", "Debe seleccionar un horario disponible.");
    if (serviciosSeleccionados.length === 0) return showAlertError("Error", "Debe añadir al menos un procedimiento.");

    setSubmitting(true);
    try {
      const dataToSend = {
        ...form,
        id_receptor: parseInt(form.id_receptor),
        id_medico: form.id_medico ? parseInt(form.id_medico) : null,
        id_consultorio: form.id_consultorio ? parseInt(form.id_consultorio) : null,
        id_tratamiento: form.id_tratamiento ? parseInt(form.id_tratamiento) : null,
        json: JSON.stringify(serviciosSeleccionados),
        hora_inicio: `${form.hora_inicio}:00`,
        hora_fin: `${form.hora_fin}:00`,
        estado: form.estado === "programada" ? 1 : (form.estado === "en_proceso" ? 2 : (form.estado === "cancelada" ? 3 : 4)),
        tipo_cita: form.tipo_cita === "evaluacion" ? 1 : (form.tipo_cita === "procedimiento" ? 2 : 3)
      };

      if (editMode) await updateCita(currentId, dataToSend);
      else await createCita(dataToSend);

      if (form.id_consultorio) {
          const newStatus = form.estado === 'en_proceso' ? 2 : 1;
          const cOriginal = consultorios.find(c => c.value === form.id_consultorio?.toString())?.original;
          if (cOriginal && cOriginal.status !== newStatus) {
              await updateConsultorioSilent(parseInt(form.id_consultorio), { nombre: cOriginal.nombre, status: newStatus });
          }
      }

      setShowModal(false);
      fetchData();
    } catch (error) { console.error(error); } 
    finally { setSubmitting(false); }
  };

  const handleQuickSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const dataToSend = {
        ...form,
        id_receptor: parseInt(form.id_receptor),
        id_medico: form.id_medico ? parseInt(form.id_medico) : null,
        id_consultorio: form.id_consultorio ? parseInt(form.id_consultorio) : null,
        id_tratamiento: form.id_tratamiento ? parseInt(form.id_tratamiento) : null,
        json: JSON.stringify(serviciosSeleccionados),
        hora_inicio: `${form.hora_inicio}:00`,
        hora_fin: `${form.hora_fin}:00`,
        estado: form.estado === "programada" ? 1 : (form.estado === "en_proceso" ? 2 : (form.estado === "cancelada" ? 3 : 4)),
        tipo_cita: form.tipo_cita === "evaluacion" ? 1 : (form.tipo_cita === "procedimiento" ? 2 : 3)
      };
      await updateCita(currentId, dataToSend);

      if (form.id_consultorio) {
          const newStatus = form.estado === 'en_proceso' ? 2 : 1;
          const cOriginal = consultorios.find(c => c.value === form.id_consultorio?.toString())?.original;
          if (cOriginal && cOriginal.status !== newStatus) {
              await updateConsultorioSilent(parseInt(form.id_consultorio), { nombre: cOriginal.nombre, status: newStatus });
          }
      }

      setShowQuickModal(false);
      fetchData();

      if (form.estado === "finalizada") {
        const citaActualizada = citas.find(c => String(c.id) === String(currentId));
        if (citaActualizada) {
          await fetchTipoDocumentos();
          setCitaAFacturar({ ...citaActualizada, json: JSON.stringify(serviciosSeleccionados) });
          setClienteFactura(citaActualizada.ClientesReceptor ? { value: citaActualizada.ClientesReceptor.id, label: citaActualizada.ClientesReceptor.nombre } : null);
          setShowFacturaModal(true);
        }
      }
    } catch (error) { console.error(error); } 
    finally { setSubmitting(false); }
  };

  const completarVentaCita = async () => {
    if (!clienteFactura || !citaAFacturar) return;
    const confirmar = await showConfirmAlert("¿Generar factura?", "Se creará el documento tributario para esta cita.");
    if (!confirmar) return;

    const procedimientos = safeParseJSON(citaAFacturar.json);
    const itemsProducts = procedimientos.map((s, index) => {
      const cantidad = Number(s.cantidad) || 1;
      const precioBruto = Number(s.price || s.precio || 0);
      const totalLinea = round2(cantidad * precioBruto);
      const taxInfo = TAX_MAP[s.Tributo?.codigo || "20"] || TAX_MAP["20"];
      const ivaValor = taxInfo.rate > 0 ? round2(totalLinea - totalLinea / (1 + taxInfo.rate)) : 0;
      const ventaGravada = round2(totalLinea - ivaValor);
      return {
        numItem: index + 1,
        idProducto: s.id,
        tipoItem: s.id_type_item ? String(s.id_type_item) : (s.TipoItem?.codigo || "2"),
        descripcion: s.name || s.description,
        cantidad,
        uniMedida: s.id_unidad_medida ? String(s.id_unidad_medida) : (s.UnidadMedida?.id ? String(s.UnidadMedida.id) : "59"),
        precioUni: round2(cantidad > 0 ? ventaGravada / cantidad : 0),
        montoDescu: 0, ventaNoSuj: 0, ventaExenta: 0,
        ventaGravada,
        tributos: [{ codigo: s.Tributo?.codigo || "20", descripcion: taxInfo.desc, valor: fmt(ivaValor) }],
        psv: 0, noGravado: 0
      };
    });

    const subtotalNum   = itemsProducts.reduce((acc, it) => acc + Number(it.ventaGravada), 0);
    const totalTributos = itemsProducts.reduce((acc, it) => acc + it.tributos.reduce((s, t) => s + Number(t.valor), 0), 0);
    const payloadVenta = {
      typeFacts: tipoFactura || "01",
      idCliente: clienteFactura.value,
      ItemsProducts: itemsProducts,
      impuestos: round2(totalTributos),
      total: round2(subtotalNum + totalTributos),
      tipoFacturacion: 1
    };

    setFacturando(true);
    try {
      let res;
      if (tipoFactura === "88") res = await FacturacionTicket(payloadVenta);
      else if (tipoFactura === "03") res = await FacturacionCCF(payloadVenta);
      else res = await FacturacionFe(payloadVenta);
      if (res) { setShowFacturaModal(false); setCitaAFacturar(null); }
    } catch (err) { console.error(err); } 
    finally { setFacturando(false); }
  };

  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(calYear, calMonth, 1).getDay();
    const offset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    const days = [];
    const date = new Date(calYear, calMonth, 1);
    while (date.getMonth() === calMonth) { days.push(new Date(date)); date.setDate(date.getDate() + 1); }
    return [...Array(offset).fill(null), ...days];
  }, [calMonth, calYear]);

  const changeMonth = (offset) => {
    let m = calMonth + offset, y = calYear;
    if (m < 0) { m = 11; y--; } else if (m > 11) { m = 0; y++; }
    setCalMonth(m); setCalYear(y);
  };

  if (loading && citas.length === 0) return <Loader />;

  return (
    <div className="p-4 sm:p-8 bg-slate-50 min-h-screen font-sans">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-black text-slate-800 tracking-tight">Citas Diarias</h1>
          <p className="text-slate-500 font-medium mt-1 uppercase text-[10px] tracking-widest">
            {fechaFiltro === todayISO ? "🟢 Hoy — " : "📅 "}{new Date(fechaFiltro + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <button onClick={() => handleOpenModal()} className="bg-indigo-600 text-white px-6 py-3 rounded-2xl hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 flex items-center gap-2 font-black text-xs uppercase tracking-widest active:scale-95">
          <FiPlus size={20} /> Agendar Nueva Cita
        </button>
      </div>

      <div className="bg-white p-4 rounded-3xl shadow-sm border border-slate-100 mb-8 flex flex-col sm:flex-row items-center gap-4">
        <div className="relative flex-1">
          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" placeholder="Buscar por paciente..." value={buscar} onChange={(e) => setBuscar(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-500 text-sm font-medium" />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="relative flex items-center">
            <FiCalendar className="absolute left-3 text-indigo-500" size={15} />
            <input type="date" value={fechaFiltro} onChange={(e) => setFechaFiltro(e.target.value)} className="pl-9 pr-4 py-3 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-500 text-sm font-bold text-slate-700 cursor-pointer" />
          </div>
          {fechaFiltro !== todayISO && <button onClick={() => setFechaFiltro(todayISO)} className="px-4 py-3 bg-indigo-50 text-indigo-600 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-indigo-100 transition-all">📅 Hoy</button>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {citas.filter(c => c.ClientesReceptor?.nombre?.toLowerCase().includes(buscar.toLowerCase())).map(cita => (
          <div key={cita.id} className="bg-white rounded-[2.5rem] p-6 shadow-sm border border-slate-100 hover:shadow-xl transition-all group relative overflow-hidden flex flex-col h-full uppercase text-[10px]">
             <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity"><FiCalendar size={80} /></div>
             <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 text-xl font-black shadow-inner">{cita.ClientesReceptor?.nombre?.charAt(0)}</div>
                <div className="min-w-0">
                   <h3 className="font-black text-slate-800 truncate leading-tight uppercase text-sm tracking-tight">{cita.ClientesReceptor?.nombre}</h3>
                   <div className="flex items-center gap-1.5 text-slate-400 font-bold mt-1 tracking-widest text-[9px]">
                      <FiClock className="text-indigo-400" /> {cita.hora_inicio?.substring(0,5)} - {cita.hora_fin?.substring(0,5)} <span className="mx-1">|</span> <FiGrid className="text-indigo-400" /> ESPACIO {cita.calendario}
                   </div>
                </div>
             </div>
             <div className="space-y-4 mb-6 flex-1">
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
                   <div className="flex items-center justify-between mb-3 border-b border-slate-200/50 pb-2">
                      <span className="font-black text-slate-400 tracking-widest">Programación</span>
                      <span className="font-black text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-100">{cita.tipo_cita}</span>
                   </div>
                   <div className="flex flex-col gap-2 font-black">
                      <div className="flex items-center gap-2 text-slate-700 font-black"><FiCalendar className="text-slate-400" /> {formatDateLocal(cita.fecha)}</div>
                      <div className="flex items-center gap-2 text-emerald-600 font-black"><FiActivity className="text-emerald-400" /> {safeParseJSON(cita.json).length} Procedimientos</div>
                   </div>
                </div>
                <div className="flex items-center justify-between px-1">
                   <span className="font-black text-slate-400 tracking-widest">Estado</span>
                   <span className={`px-3 py-1 rounded-full font-black tracking-widest border ${
                      cita.estado === "programada" ? "bg-amber-50 text-amber-600 border-amber-200" :
                      cita.estado === "en_proceso" ? "bg-indigo-50 text-indigo-600 border-indigo-200" :
                      cita.estado === "finalizada" ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-rose-50 text-rose-600 border-rose-200"
                   }`}>{cita.estado}</span>
                </div>
             </div>
             <div className="flex gap-2 mt-auto">
                <button onClick={() => handleOpenQuickModal(cita)} className="w-[45px] bg-slate-100 text-slate-500 py-4 rounded-2xl hover:bg-slate-200 transition-all flex items-center justify-center hover:text-indigo-600"><FiActivity size={18} /></button>
                <button onClick={() => handleOpenModal(cita)} className="flex-1 bg-slate-800 text-white py-4 rounded-2xl font-black uppercase tracking-widest hover:bg-slate-900 transition-all flex items-center justify-center gap-2 shadow-lg shadow-slate-100"><FiEdit3 /> Editar Detalles</button>
             </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[100] flex justify-center items-center p-2 sm:p-4 animate-fade-in">
          <div className="bg-white rounded-[2rem] sm:rounded-[3rem] shadow-2xl w-full max-w-4xl h-full max-h-[92vh] flex flex-col overflow-hidden animate-slide-up border border-white">
            <div className="p-6 sm:p-8 bg-slate-50 flex items-center justify-between shrink-0 border-b border-slate-100">
               <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-indigo-600 text-white flex items-center justify-center shadow-lg shadow-indigo-100"><FiPlus size={24} /></div>
                  <div>
                    <h2 className="text-2xl font-black text-slate-800 tracking-tight">{editMode ? "Actualizar" : "Nueva"} Cita</h2>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-0.5">Gestión de Agenda Clínica</p>
                  </div>
               </div>
               <button onClick={() => setShowModal(false)} className="p-3 bg-white text-slate-400 rounded-2xl hover:bg-slate-100 border border-slate-100"><FiChevronLeft /></button>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar p-6 sm:p-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                   <div className="space-y-8">
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-2 pl-1"><FiUser className="text-indigo-500" /> PACIENTE</label>
                         <ReactSelect options={pacientes} value={pacientes.find(p => p.value === form.id_receptor?.toString()) || null} onChange={(val) => handleSelectChange(val, "id_receptor")} placeholder="Buscar paciente..." isClearable menuPortalTarget={document.body} styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }), control: b => ({ ...b, borderRadius: '1.25rem', padding: '6px', backgroundColor: '#f8fafc', border: 'none' }) }} />
                      </div>
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-2 pl-1"><FiLayers className="text-indigo-500" /> TRATAMIENTO</label>
                         <ReactSelect options={tratamientosPaciente.map(t => ({ value: t.id.toString(), label: t.OdontologiaTipoTratamiento?.nombre || "Tratamiento" }))} value={tratamientosPaciente.filter(t => t.id.toString() === form.id_tratamiento).map(t => ({ value: t.id.toString(), label: t.OdontologiaTipoTratamiento?.nombre || "Tratamiento" }))[0] || null} onChange={(val) => handleSelectChange(val, "id_tratamiento")} placeholder="Vincular tratamiento..." isClearable isDisabled={!form.id_receptor} menuPortalTarget={document.body} styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }), control: b => ({ ...b, borderRadius: '1.25rem', padding: '6px', backgroundColor: '#f8fafc', border: 'none' }) }} />
                      </div>
                      <div className="space-y-4">
                         <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-2 pl-1"><FiLayers className="text-indigo-500" /> AGREGAR PROCEDIMIENTOS</label>
                         <ReactSelect options={servicios.map(s => ({ value: s.id.toString(), label: s.name, data: { ...s } }))} value={null} onChange={handleAgregarProcedimiento} placeholder="Buscar servicio..." menuPortalTarget={document.body} styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }), control: b => ({ ...b, borderRadius: '1.25rem', padding: '6px', backgroundColor: '#f8fafc', border: 'none' }) }} />
                         <div className="bg-slate-50/50 rounded-[2.5rem] p-6 border border-slate-100 space-y-3">
                            {serviciosSeleccionados.map(s => (
                               <div key={s.id} className="bg-white p-4 rounded-2xl flex items-center justify-between border border-slate-100 shadow-sm">
                                  <div className="flex items-center gap-4">
                                     <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-black text-xs">{s.cantidad}</div>
                                     <div><p className="text-xs font-black text-slate-700 uppercase tracking-tight mb-1">{s.name}</p><p className="text-[9px] text-slate-400 font-bold uppercase tracking-[0.2em]">{s.duracion_minutos || 0} MIN</p></div>
                                  </div>
                                  <div className="flex items-center gap-4">
                                     <div className="flex items-center bg-slate-100 p-1 rounded-xl">
                                        <button onClick={() => handleCantidadChange(s.id, s.cantidad - 1)} className="w-6 h-6 rounded-lg bg-white shadow-sm">-</button>
                                        <span className="w-8 text-center text-xs font-black">{s.cantidad}</span>
                                        <button onClick={() => handleCantidadChange(s.id, s.cantidad + 1)} className="w-6 h-6 rounded-lg bg-white shadow-sm">+</button>
                                     </div>
                                     <button onClick={() => handleEliminarProcedimiento(s.id)} className="text-slate-300 hover:text-rose-500"><FiPlus className="rotate-45" size={20} /></button>
                                  </div>
                               </div>
                            ))}
                         </div>
                      </div>
                   </div>
                   <div className="space-y-8">
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-2 pl-1"><FiUser className="text-indigo-500" /> PROFESIONAL</label>
                         <ReactSelect options={doctores} value={doctores.find(d => d.value === form.id_medico?.toString()) || null} onChange={(val) => handleSelectChange(val, "id_medico")} placeholder="Seleccionar médico..." isClearable menuPortalTarget={document.body} styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }), control: b => ({ ...b, borderRadius: '1.25rem', padding: '6px', backgroundColor: '#f8fafc', border: 'none' }) }} />
                      </div>
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-2 pl-1"><FiBriefcase className="text-indigo-500" /> CONSULTORIO</label>
                         <ReactSelect options={consultorios.filter(c => c.original?.status === 1 || c.value === form.id_consultorio?.toString())} value={consultorios.find(c => c.value === form.id_consultorio?.toString()) || null} onChange={(val) => handleSelectChange(val, "id_consultorio")} placeholder="Asignar consultorio..." isClearable menuPortalTarget={document.body} styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }), control: b => ({ ...b, borderRadius: '1.25rem', padding: '6px', backgroundColor: '#f8fafc', border: 'none' }) }} />
                      </div>
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-2 pl-1"><FiCalendar className="text-indigo-500" /> FECHA Y HORARIO</label>
                         <button type="button" onClick={() => { setCalMonth(new Date(form.fecha).getMonth()); setCalYear(new Date(form.fecha).getFullYear()); setSlotStep(0); setShowSlotModal(true); }} className="w-full text-left p-6 rounded-[2rem] bg-indigo-50 border-2 border-dashed border-indigo-200 hover:border-indigo-400 transition-all font-black">
                            {form.hora_inicio ? (
                               <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center text-indigo-600 shadow-sm"><FiCalendar size={24} /></div>
                                  <div><p className="text-[9px] text-indigo-600 uppercase tracking-widest mb-1">Agenda confirmada</p><h4 className="text-base text-slate-800">{formatDateLocal(form.fecha)}</h4><div className="text-xs text-slate-500 flex items-center gap-1"><FiClock /> {form.hora_inicio} - {form.hora_fin} | ESPACIO {form.calendario}</div></div>
                               </div>
                            ) : <div className="text-center py-2"><FiClock size={20} className="mx-auto mb-2 text-indigo-300" /><p className="text-[10px] text-slate-500 uppercase tracking-widest">Elegir Horario y Espacio</p></div>}
                         </button>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                         <div className="space-y-2">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block ml-1">ESTADO</label>
                            <ReactSelect options={[{ value: "programada", label: "Programada" }, { value: "en_proceso", label: "En Proceso" }, { value: "cancelada", label: "Cancelada" }, { value: "finalizada", label: "Finalizada" }]} value={{ value: form.estado, label: form.estado === "en_proceso" ? "En Proceso" : form.estado.charAt(0).toUpperCase() + form.estado.slice(1) }} onChange={(val) => handleSelectChange(val, "estado")} menuPortalTarget={document.body} styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }), control: b => ({ ...b, borderRadius: '1.25rem', padding: '6px', backgroundColor: '#f8fafc', border: 'none' }) }} />
                         </div>
                         <div className="space-y-2">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block ml-1">CATEGORÍA</label>
                            <ReactSelect options={[{ value: "procedimiento", label: "Procedimiento" }, { value: "evaluacion", label: "Evaluación" }, { value: "control", label: "Control" }]} value={{ value: form.tipo_cita, label: form.tipo_cita.charAt(0).toUpperCase() + form.tipo_cita.slice(1) }} onChange={(val) => handleSelectChange(val, "tipo_cita")} menuPortalTarget={document.body} styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }), control: b => ({ ...b, borderRadius: '1.25rem', padding: '6px', backgroundColor: '#f8fafc', border: 'none' }) }} />
                         </div>
                      </div>
                      <textarea value={form.observaciones} onChange={(e) => setForm(p => ({ ...p, observaciones: e.target.value }))} className="w-full bg-slate-50 border-none rounded-[2rem] p-6 text-xs font-medium h-[100px] shadow-inner" placeholder="Observaciones..." />
                   </div>
                </div>
            </div>
            <div className="p-6 sm:p-10 border-t bg-slate-50/80 flex justify-end gap-4">
                <button onClick={() => setShowModal(false)} className="px-10 py-4 text-slate-500 font-black text-xs uppercase tracking-widest">Cerrar</button>
                <button onClick={handleSubmit} disabled={submitting} className="bg-indigo-600 text-white px-14 py-4 rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-2xl hover:bg-slate-900 transition-all flex items-center justify-center gap-3 disabled:opacity-50">
                   {submitting ? "..." : <><FiCheckCircle size={20} /> {editMode ? "Actualizar" : "Confirmar"}</>}
                </button>
            </div>
          </div>
        </div>
      )}

      {showSlotModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[110] flex justify-center items-center p-4 animate-fade-in">
           <div className="bg-white rounded-[3rem] shadow-2xl w-full max-w-5xl h-full max-h-[85vh] flex flex-col md:flex-row overflow-hidden animate-slide-up">
              <div className={`flex-1 p-6 sm:p-12 overflow-y-auto custom-scrollbar border-r border-slate-50 ${slotStep === 0 ? "flex flex-col" : "hidden"} md:flex md:flex-col`}>
                 <div className="mb-10 flex items-center justify-between">
                    <div><h3 className="text-2xl font-black text-slate-800 tracking-tight leading-none mb-1">Elegir Fecha</h3><p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Calendario Clínico</p></div>
                    <div className="flex items-center gap-1.5 bg-slate-100 p-1.5 rounded-2xl">
                       <button onClick={() => changeMonth(-1)} className="p-2.5 bg-white rounded-xl shadow-sm"><FiChevronLeft size={20} /></button>
                       <span className="px-5 font-black text-slate-800 text-xs uppercase tracking-widest">{new Date(calYear, calMonth).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}</span>
                       <button onClick={() => changeMonth(1)} className="p-2.5 bg-white rounded-xl shadow-sm"><FiChevronRight size={20} /></button>
                    </div>
                 </div>
                 <div className="grid grid-cols-7 gap-2 text-center mb-6">{['LU','MA','MI','JU','VI','SA','DO'].map(d => <span key={d} className="text-[9px] font-black text-slate-300 tracking-[0.2em]">{d}</span>)}</div>
                 <div className="grid grid-cols-7 gap-3 sm:gap-4">
                    {calendarDays.map((day, i) => {
                       if (!day) return <div key={i} className="h-10 w-10 sm:h-14 sm:w-14"></div>;
                       const dStr = formatDateToISO(day);
                       return (
                          <button key={i} disabled={dStr < todayISO} onClick={() => { setForm(p => ({ ...p, fecha: dStr })); setSlotStep(1); }} className={`h-10 w-10 sm:h-14 sm:w-14 rounded-2xl text-[11px] font-black transition-all border relative ${dStr === form.fecha ? "bg-indigo-600 text-white border-indigo-600 scale-110 shadow-xl" : dStr < todayISO ? "bg-slate-50 text-slate-200 border-transparent cursor-not-allowed" : "bg-white text-slate-600 border-slate-100 hover:border-indigo-400 island-hover"}`}>{day.getDate()}</button>
                       );
                    })}
                 </div>
              </div>
              <div className={`w-full md:w-[450px] bg-slate-50/50 p-6 sm:p-12 flex flex-col ${slotStep === 1 ? "flex" : "hidden"} md:flex`}>
                 <div className="mb-8 flex items-center justify-between">
                    <div><h3 className="text-2xl font-black text-slate-800 tracking-tight leading-none mb-1">Horarios</h3><p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{formatDateLocal(form.fecha)}</p></div>
                    <button onClick={() => setSlotStep(0)} className="md:hidden flex items-center gap-2 px-4 py-2 bg-white text-indigo-600 rounded-xl text-[10px] font-black border border-indigo-100 uppercase tracking-widest"><FiChevronLeft /> Volver</button>
                 </div>
                 <div className="space-y-4 flex-1 overflow-y-auto custom-scrollbar pr-2 pb-6">
                    {slotsDisponibles.map((s, idx) => (
                       <div key={idx} className={`p-4 rounded-[2rem] border transition-all ${s.disponible ? "bg-white border-white shadow-sm" : "bg-slate-100 border-transparent opacity-40"}`}>
                          <div className="flex items-center justify-between mb-4 px-2">
                             <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400"><FiClock size={14} /></div><div><h5 className="text-xs font-black">{s.time}</h5><p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Inicia Turno</p></div></div>
                             <span className={`text-[8px] font-black px-2 py-1 rounded-full uppercase tracking-widest ${s.disponible ? "bg-indigo-50 text-indigo-600" : "bg-rose-50 text-rose-600"}`}>{s.motivo}</span>
                          </div>
                          
                          {s.disponible && (
                            <div className="grid grid-cols-2 gap-2">
                               {Array.from({ length: config.calendarios_cantidad }).map((_, i) => {
                                 const calId = i + 1;
                                 const isAvail = s.disponibles.includes(calId);
                                 const isSelected = form.hora_inicio === s.time && form.calendario === calId;
                                 return (
                                   <button 
                                      key={calId}
                                      disabled={!isAvail}
                                      onClick={() => setForm(p => ({ ...p, hora_inicio: s.time, hora_fin: s.timeEndStr, calendario: calId }))}
                                      className={`py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest border transition-all
                                        ${isSelected ? "bg-indigo-600 text-white border-indigo-600 shadow-lg scale-[1.03]" : isAvail ? "bg-slate-50 text-slate-600 border-slate-100 hover:bg-indigo-50 hover:text-indigo-600" : "bg-slate-200 text-slate-400 border-transparent opacity-30 cursor-not-allowed"}
                                      `}
                                   >
                                      Espacio {calId}
                                   </button>
                                 );
                               })}
                            </div>
                          )}
                       </div>
                    ))}
                 </div>
                 <div className="pt-6">
                    <button onClick={() => { if (!form.hora_inicio) return showAlertError("Elegir horario", "Selecciona un espacio disponible."); setShowSlotModal(false); }} className="w-full bg-slate-800 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:bg-slate-900 transition-all flex items-center justify-center gap-3">
                       <FiCheckCircle size={18} /> Confirmar Selección
                    </button>
                 </div>
              </div>
           </div>
        </div>
      )}

      {showQuickModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100] flex justify-center items-center p-4 animate-fade-in">
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-sm overflow-hidden animate-slide-up border border-white">
            <div className="p-6 bg-slate-50 border-b flex justify-between">
              <div><h3 className="font-black text-slate-800 text-lg">Estado Directo</h3><p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-0.5">Espacio: {form.calendario}</p></div>
              <button onClick={() => setShowQuickModal(false)} className="p-2 bg-white text-slate-400 rounded-xl border border-slate-100"><FiChevronLeft /></button>
            </div>
            <div className="p-6 space-y-6">
               <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-2 pl-1"><FiBriefcase className="text-indigo-500" /> CONSULTORIO</label>
                  <ReactSelect options={consultorios.filter(c => c.original?.status === 1 || c.value === form.id_consultorio?.toString())} value={consultorios.find(c => c.value === form.id_consultorio?.toString()) || null} onChange={(val) => handleSelectChange(val, "id_consultorio")} placeholder="Sin asignar..." isClearable menuPortalTarget={document.body} styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }), control: b => ({ ...b, borderRadius: '1rem', padding: '4px', backgroundColor: '#f8fafc', border: 'none' }) }} />
               </div>
               <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-2 pl-1"><FiUser className="text-indigo-500" /> MÉDICO</label>
                  <ReactSelect options={doctores} value={doctores.find(d => d.value === form.id_medico?.toString()) || null} onChange={(val) => handleSelectChange(val, "id_medico")} placeholder="Sin asignar..." isClearable menuPortalTarget={document.body} styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }), control: b => ({ ...b, borderRadius: '1rem', padding: '4px', backgroundColor: '#f8fafc', border: 'none' }) }} />
               </div>
               <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-2 pl-1"><FiActivity className="text-indigo-500" /> PROGRESO</label>
                  <ReactSelect options={[{ value: "programada", label: "Programada" }, { value: "en_proceso", label: "En Proceso" }, { value: "cancelada", label: "Cancelada" }, { value: "finalizada", label: "Finalizada" }]} value={{ value: form.estado, label: form.estado === "en_proceso" ? "En Proceso" : form.estado.charAt(0).toUpperCase() + form.estado.slice(1) }} onChange={(val) => handleSelectChange(val, "estado")} menuPortalTarget={document.body} styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }), control: b => ({ ...b, borderRadius: '1rem', padding: '4px', backgroundColor: '#f8fafc', border: 'none' }) }} />
               </div>
               <button onClick={handleQuickSubmit} disabled={submitting} className="w-full bg-indigo-600 text-white rounded-xl py-3.5 font-black text-xs uppercase tracking-widest flex justify-center items-center gap-2 shadow-lg disabled:opacity-50">
                  {submitting ? "..." : <><FiCheckCircle size={18} /> Aplicar Cambios</>}
               </button>
            </div>
          </div>
        </div>
      )}

      {showFacturaModal && citaAFacturar && (() => {
        const procs = safeParseJSON(citaAFacturar.json), total = procs.reduce((a, s) => a + Number(s.price || s.precio || 0) * (s.cantidad || 1), 0);
        return (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[120] flex justify-center items-center p-2 sm:p-4 animate-fade-in">
            <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg flex flex-col overflow-hidden animate-slide-up">
              <div className="p-6 bg-gradient-to-r from-emerald-600 to-teal-500 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center text-white"><FiDollarSign size={22} /></div><div><h3 className="text-white font-black text-base leading-tight">Factura Cita #{citaAFacturar.id}</h3><p className="text-emerald-100 text-[10px] font-bold uppercase tracking-widest mt-0.5">{citaAFacturar.ClientesReceptor?.nombre}</p></div></div>
                <button onClick={() => setShowFacturaModal(false)} className="w-9 h-9 rounded-xl bg-white/20 text-white"><FiPlus className="rotate-45" size={20} /></button>
              </div>
              <div className="p-6 space-y-5 overflow-y-auto custom-scrollbar flex-1">
                <div className="space-y-2"><label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Tipo Documento</label><select value={tipoFactura} onChange={(e) => setTipoFactura(e.target.value)} className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-4 py-2.5 font-bold text-sm text-slate-700 outline-none focus:border-emerald-300 transition-colors uppercase">{tipoDocumentos.map(doc => { const type = doc.TipoDocumento || doc.typeFact || doc; return <option key={doc.id} value={type.codigo}>{type.name}</option>; })}</select></div>
                <div className="space-y-2"><label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Receptor</label><div className="bg-slate-50 rounded-xl px-4 py-3 border border-slate-100 flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center font-black text-sm">{citaAFacturar.ClientesReceptor?.nombre?.charAt(0) || "?"}</div><div className="flex-1"><p className="text-sm font-bold text-slate-800">{citaAFacturar.ClientesReceptor?.nombre || "—"}</p><p className="text-[10px] text-slate-400">{citaAFacturar.ClientesReceptor?.correo || ""}</p></div></div><ReactSelect options={pacientes.map(p => ({ value: p.original?.id ?? p.value, label: p.label }))} value={clienteFactura} onChange={setClienteFactura} placeholder="Cambiar receptor..." isClearable styles={{ control: b => ({ ...b, borderRadius: '0.75rem', padding: '4px', backgroundColor: '#f8fafc', border: 'none' }) }} /></div>
                <div className="space-y-2">
                   <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Resumen</p>
                   <div className="space-y-2">{procs.map((s, i) => (<div key={i} className="flex justify-between bg-slate-50 rounded-xl px-4 py-2.5 border border-slate-100"><p className="text-xs font-semibold text-slate-700">{s.name}</p><span className="text-xs font-bold text-emerald-600">${(Number(s.price || s.precio || 0) * (s.cantidad || 1)).toFixed(2)}</span></div>))}</div>
                   <div className="flex justify-between bg-emerald-600 text-white rounded-xl px-4 py-3 shadow-lg"><span className="text-xs font-black uppercase tracking-widest">Total Cobro</span><span className="text-lg font-black">${total.toFixed(2)}</span></div>
                </div>
              </div>
              <div className="p-5 border-t bg-slate-50 flex gap-3 justify-end shrink-0">
                <button onClick={() => setShowFacturaModal(false)} disabled={facturando} className="px-5 py-2.5 rounded-xl border text-slate-700 font-semibold text-sm hover:bg-white transition-colors">Omitir</button>
                <button onClick={completarVentaCita} disabled={facturando || !clienteFactura} className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-sm flex items-center gap-2 shadow-lg transition-all disabled:opacity-50">
                  {facturando ? <Loader small white /> : <><FiCheckCircle size={18} /> Emitir Documento</>}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
        .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
        .animate-slide-up { animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </div>
  );
};

export default CitasOdontologia;
